import * as Sentry from '@sentry/react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { safeLocalStorage, safeSessionStorage } from '../utils/safeStorage';
import type { EffectivePermissionsMap, EmpresaDoUsuario, Plan, PlanFeature, PermissionAction, PermissionModule, User } from '../types';
import {
    VISAO_CONSOLIDADA,
    corrigirVisaoSemFeature,
    negocioPadrao,
    resolverFilialInicial,
    rotaBloqueadaNaVisao,
    valorSalvoDaVisaoConsolidada,
} from './filialAtiva';
import {
    EMPRESA_ATIVA_KEY,
    bloqueioDaTela,
    filiaisDaEmpresa,
    marcarEmpresaBloqueada,
    podeGerirAcessosNaEmpresa,
    resolverEmpresaInicial,
    valorSalvoDaEmpresa,
    type BloqueioDaTela,
    type EstadoDaEmpresa,
} from './empresaAtiva';

/**
 * Token da sessao salvo no navegador. Com o armazenamento bloqueado (aba
 * anonima, iframe, cookies de terceiros bloqueados) ler localStorage lanca
 * SecurityError e o boot ficava preso no carregamento (LTQ-WEB-3W); aqui vira
 * "sem sessao".
 */
export function tokenSalvo(): string | null {
    return safeLocalStorage.getItem('accessToken');
}

/** Tira o guard do toast de vencimento para ele reaparecer no proximo login. */
export function limparAvisoDeVencimento(): void {
    safeSessionStorage.keys()
        .filter(k => k.startsWith('expiry-toast-shown:'))
        .forEach(k => safeSessionStorage.removeItem(k));
}

type Filial = { id: string; name: string; empresaId: string; isActive?: boolean; role?: 'ADMIN' | 'EDITOR' | 'VIEWER'; empresa: { id: string; name: string } };

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isAdmin: boolean;
    isGerente: boolean;
    isDemo: boolean;
    currentPlan: Plan | null;
    login: (email: string, password: string) => Promise<void>;
    register: (data: { name: string; email: string; password: string; whatsapp: string }) => Promise<void>;
    logout: () => void;
    hasFeature: (feature: PlanFeature) => boolean;
    canAccess: (route: string) => boolean;
    isDemoRoute: (route: string) => boolean;
    filialAtiva: { id: string; name: string } | null;
    filiais: Filial[];
    selecionarFilial: (filialId: string) => Promise<void>;
    isLoadingFiliais: boolean;
    carregarFiliais: () => Promise<void>;
    markOnboardingComplete: () => Promise<void>;
    updateUser: (data: Partial<User>) => void;
    // Gestão de Perfis de Acesso — matriz efetiva de permissões do usuário
    // na filial ativa (ver SPEC-gestao-perfis-acesso.md, seção 6.1).
    permissions: EffectivePermissionsMap | null;
    isLoadingPermissions: boolean;
    hasPermission: (module: PermissionModule, action: PermissionAction) => boolean;
    // Empresas de que o usuario participa e a empresa em que ele esta operando.
    // `filiais` sao so as da empresa ativa. Usuarios e Perfis de acesso so
    // aparecem para o dono da empresa ativa.
    empresas: EmpresaDoUsuario[];
    empresaAtiva: EmpresaDoUsuario | null;
    souDonoDaEmpresaAtiva: boolean;
    /** Abas Usuarios e Perfis de acesso: dono da empresa ativa com a feature `usuarios` e vaga no plano. */
    podeGerirAcessos: boolean;
    /** Troca de empresa: abre a Matriz dela e volta para o inicio. */
    selecionarEmpresa: (empresaId: string) => void;
    /** O que bloqueia a tela na empresa ativa (ver empresaAtiva.ts). */
    bloqueio: BloqueioDaTela;
    /** Nao participa de nenhuma empresa: so pode criar a propria. */
    semEmpresa: boolean;
    criarMinhaEmpresa: () => Promise<void>;
    /** Entra com uma sessao ja emitida pela API (cadastro pelo convite). */
    entrarComSessao: (response: Awaited<ReturnType<typeof api.login>>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

const routeToFeature: Record<string, PlanFeature> = {
    '/home': 'dashboard',
    '/lancar-receita': 'transactions',
    '/lancar-despesa': 'transactions',
    '/lancamentos': 'transactions',
    '/cashflow': 'cashflow',
    '/graficos': 'graficos',
    '/reports': 'reports',
    '/plano-contas': 'accounts',
    '/importacoes': 'import',
    // Configuracoes abre em qualquer plano; so as abas Usuarios e Perfis de
    // acesso dependem do plano (feature `usuarios`, ver podeGerirAcessos).
};

// Rotas integradas com o backend (para usuário demo)
const DEMO_ALLOWED_ROUTES = [
    '/home',
    '/graficos',
    '/plano-contas',
    '/lancamentos',
    '/lancar-receita',
    '/lancar-despesa',
    '/cashflow',
    '/importar-lancamentos',
    '/reports',
    '/help',
    '/settings',
];

const DEMO_EMAIL = 'demo@lqt.com';

/** Token de convite guardado enquanto a pessoa entra com a conta para aceitar. */
export const CONVITE_PENDENTE_KEY = 'convite-pendente';

/**
 * Para onde ir depois de entrar quando a pessoa veio de um link de convite
 * (volta para aceitar). Null quando nao ha convite pendente.
 */
export function destinoDoConvitePendente(): string | null {
    const token = safeSessionStorage.getItem(CONVITE_PENDENTE_KEY);
    return token ? `/convite?token=${encodeURIComponent(token)}` : null;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [filialAtiva, setFilialAtiva] = useState<{ id: string; name: string } | null>(null);
    // Negocios de todas as empresas; o contexto expoe so os da empresa ativa.
    const [todasAsFiliais, setTodasAsFiliais] = useState<Filial[]>([]);
    const [empresas, setEmpresas] = useState<EmpresaDoUsuario[]>([]);
    const [empresaAtivaId, setEmpresaAtivaId] = useState<string | null>(null);
    const [contaSuspensa, setContaSuspensa] = useState(false);
    // A consulta de empresas respondeu (lista vazia so vale como "sem empresa" assim).
    const [empresasCarregadas, setEmpresasCarregadas] = useState(false);
    const [isLoadingFiliais, setIsLoadingFiliais] = useState(false);
    const [permissions, setPermissions] = useState<EffectivePermissionsMap | null>(null);
    const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
    const navigate = useNavigate();

    const isAdmin = user?.role === 'ADMIN';
    const isGerente = user?.role === 'GERENTE' || user?.role === 'ADMIN';
    const empresaAtiva = empresas.find((e) => e.id === empresaAtivaId) ?? null;
    const filiais = useMemo(() => filiaisDaEmpresa(todasAsFiliais, empresaAtiva?.id), [todasAsFiliais, empresaAtiva?.id]);
    const isDemo = user?.email === DEMO_EMAIL;

    // Identifica o usuario no Sentry. Um unico efeito cobre todos os caminhos
    // que mexem em `user` (login, cadastro, restauracao de sessao no boot e
    // logout), em vez de espalhar chamadas por cada um deles.
    useEffect(() => {
        if (user) {
            Sentry.setUser({ id: user.id, email: user.email, username: user.name });
        } else {
            Sentry.setUser(null);
        }
    }, [user]);

    // Bloqueio no meio da sessao (api.ts dispara os eventos). A conta inteira
    // suspensa (sem outra empresa liberada) vira tela de pagamento; empresa
    // suspensa ou inativa marca so ela, e a pessoa pode trocar de empresa.
    useEffect(() => {
        const contaInteira = () => {
            setContaSuspensa(true);
            // Guard: only update if not already suspended to avoid infinite re-renders
            setUser(prev => (prev && prev.status !== 'suspended') ? { ...prev, status: 'suspended' } : prev);
        };
        const daEmpresa = (event: Event) => {
            const { empresaId, estado } = (event as CustomEvent<{ empresaId: string; estado: EstadoDaEmpresa }>).detail;
            setEmpresas(prev => (prev.some(e => e.id === empresaId && e.estado !== estado)
                ? marcarEmpresaBloqueada(prev, empresaId, estado)
                : prev));
        };
        window.addEventListener('account-suspended', contaInteira);
        window.addEventListener('empresa-bloqueada', daEmpresa);
        return () => {
            window.removeEventListener('account-suspended', contaInteira);
            window.removeEventListener('empresa-bloqueada', daEmpresa);
        };
    }, []);

    const carregarFiliais = useCallback(async () => {
        setIsLoadingFiliais(true);
        try {
            const [data, minhasEmpresas] = await Promise.all([
                api.getMinhasFiliais(),
                // Falhar aqui nao pode derrubar as filiais: sem empresas, vale o
                // comportamento de antes (todos os negocios, plano do usuario, sem
                // as telas de dono) e o backend usa a empresa propria.
                api.getMinhasEmpresas().catch(() => null),
            ]);
            setTodasAsFiliais(data);
            setEmpresas(minhasEmpresas ?? []);
            setEmpresasCarregadas(minhasEmpresas !== null);
            setContaSuspensa(false);

            // Regras em empresaAtiva.ts: a escolhida, senao a propria.
            const { ativa: empresa, regravar: regravarEmpresa } = resolverEmpresaInicial(
                minhasEmpresas ?? [],
                safeLocalStorage.getItem(EMPRESA_ATIVA_KEY),
            );
            setEmpresaAtivaId(empresa?.id ?? null);
            if (empresa && regravarEmpresa) {
                safeLocalStorage.setItem(EMPRESA_ATIVA_KEY, valorSalvoDaEmpresa(empresa.id));
            } else if (!empresa) {
                // Sem cabecalho de empresa o backend decide pelo negocio pedido.
                safeLocalStorage.removeItem(EMPRESA_ATIVA_KEY);
            }
            const daEmpresa = filiaisDaEmpresa(data, empresa?.id);

            // Conta sem nenhuma filial: o admin criado pelo admin:create (unico
            // fluxo que nao provisiona empresa/filial) ou o convidado que saiu de
            // todas as empresas. Nao existe contexto de filial para assumir:
            // manter filialAtiva nula impede o FinanceContext de disparar as
            // chamadas de dashboard, que o FilialGuard rejeita com 403.
            if (daEmpresa.length === 0) {
                setFilialAtiva(null);
                safeLocalStorage.removeItem('filialAtiva');
                return;
            }

            // Regras em filialAtiva.ts: padrao e a Matriz, e "Todos os negocios"
            // salvo so volta se foi escolhido no seletor. Negocio salvo de outra
            // empresa nao existe nesta lista e cai na Matriz dela.
            const { ativa, regravar } = resolverFilialInicial(daEmpresa, safeLocalStorage.getItem('filialAtiva'));
            setFilialAtiva(ativa);
            if (regravar && ativa) {
                safeLocalStorage.setItem('filialAtiva', JSON.stringify(ativa));
            }
        } catch {
            // silencia, filiais ficam vazias
        } finally {
            setIsLoadingFiliais(false);
        }
    }, []);

    const carregarPermissoes = useCallback(async () => {
        setIsLoadingPermissions(true);
        try {
            const data = await api.getPermissions();
            setPermissions(data);
        } catch {
            // silencia — hasPermission trata `permissions === null` negando tudo,
            // exceto para ADMIN (bypass), então falhar fechado é seguro aqui.
            setPermissions(null);
        } finally {
            setIsLoadingPermissions(false);
        }
    }, []);

    // Recarrega a matriz de permissões sempre que a filial ativa muda —
    // perfil/exceções são por vínculo usuário↔filial (spec, seção 2.2), então
    // trocar de filial pode mudar completamente o que o usuário pode fazer.
    useEffect(() => {
        if (user && filialAtiva) {
            carregarPermissoes();
        } else {
            setPermissions(null);
        }
    }, [user, filialAtiva, carregarPermissoes]);

    const carregarPerfil = useCallback(async () => {
        const profile = await api.getProfile();
        setUser({
            id: profile.id,
            name: profile.name,
            email: profile.email,
            role: profile.role,
            status: profile.status?.toLowerCase() as 'active' | 'inactive' | 'suspended',
            planId: profile.planId,
            onboardingCompletedAt: profile.onboardingCompletedAt ?? null,
            surveyOnboardingRequired: profile.surveyOnboardingRequired ?? false,
            surveyOnboardingCompletedAt: profile.surveyOnboardingCompletedAt ?? null,
        });
        if (profile.plan) {
            setCurrentPlan({
                id: profile.plan.id,
                name: profile.plan.name,
                description: profile.plan.description || '',
                price: profile.plan.price,
                interval: profile.plan.interval ?? 'monthly',
                features: profile.plan.features || [],
                isActive: profile.plan.isActive,
                maxAccounts: profile.plan.maxAccounts ?? 0,
                maxTransactionsPerMonth: profile.plan.maxTransactionsPerMonth ?? 0,
                createdAt: profile.plan.createdAt ?? '',
                updatedAt: profile.plan.updatedAt ?? '',
            });
        }
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            const token = tokenSalvo();
            if (token) {
                try {
                    await carregarPerfil();
                    await carregarFiliais();
                } catch {
                    safeLocalStorage.removeItem('accessToken');
                    safeLocalStorage.removeItem('refreshToken');
                }
            }
            setIsInitialized(true);
        };
        checkAuth();
    }, [carregarPerfil, carregarFiliais]);

    // Quem so participa de empresas de outros cria a propria (freemium) e passa
    // a operar nela.
    const criarMinhaEmpresa = useCallback(async () => {
        const { empresaId } = await api.criarMinhaEmpresa();
        safeLocalStorage.setItem(EMPRESA_ATIVA_KEY, valorSalvoDaEmpresa(empresaId));
        safeLocalStorage.removeItem('filialAtiva');
        await carregarPerfil();
        await carregarFiliais();
        navigate('/home');
    }, [carregarPerfil, carregarFiliais, navigate]);

    // Sem nenhuma empresa (convidado que saiu de todas, ou que nunca teve a
    // propria): a tela oferece criar a dele. A equipe LTQ nao opera empresa.
    const semEmpresa = !!user && !isAdmin && empresasCarregadas && empresas.length === 0 && todasAsFiliais.length === 0;

    // Features e limites valem pelo plano de quem paga a empresa ativa (o dono).
    // Para o proprio dono e o plano dele mesmo; antes das empresas carregarem, ou
    // se a consulta falhar, vale o plano do usuario, como sempre foi.
    const featuresVigentes: string[] | null = empresaAtiva
        ? empresaAtiva.plano?.features ?? null
        : currentPlan?.features ?? null;

    const hasFeature = useCallback((feature: PlanFeature): boolean => {
        if (!user) return false;
        if (isAdmin) return true;
        if (!featuresVigentes) return false;
        return featuresVigentes.includes(feature);
    }, [user, isAdmin, featuresVigentes]);

    // Sem a feature de filiais o seletor nao aparece, entao quem estiver na visao
    // consolidada (escolha de quando tinha a feature, por exemplo) ficaria preso
    // sem poder lancar. Plano e usuario chegam juntos do /auth/profile, antes das
    // filiais, entao aqui o hasFeature ja reflete o plano real.
    useEffect(() => {
        if (!user) return;
        const destino = corrigirVisaoSemFeature(filialAtiva, filiais, hasFeature('filiais'));
        if (!destino) return;
        setFilialAtiva(destino);
        safeLocalStorage.setItem('filialAtiva', JSON.stringify(destino));
    }, [user, filialAtiva, filiais, hasFeature]);

    // Gestão de Perfis de Acesso (spec, seção 4.2): ADMIN (equipe LTQ) tem
    // bypass total, igual ao PermissionsGuard do backend. Para os demais,
    // nega por padrão enquanto a matriz ainda não carregou ou falhou.
    const hasPermission = useCallback((module: PermissionModule, action: PermissionAction): boolean => {
        if (!user) return false;
        if (isAdmin) return true;
        if (!permissions) return false;
        return Boolean(permissions[module]?.[action]);
    }, [user, isAdmin, permissions]);

    const canAccess = useCallback((route: string): boolean => {
        if (!user) return false;
        // "Todos os negocios" e visao consolidada: mostra numeros de todas as
        // filiais, mas nao tem uma filial de destino para receber lancamento
        // (antes ia calado para a primeira, quase sempre a Matriz).
        if (rotaBloqueadaNaVisao(route, filialAtiva?.id)) return false;
        if (isAdmin) return true;
        if (route.startsWith('/admin')) return false;
        if (isDemo) return DEMO_ALLOWED_ROUTES.includes(route);
        const feature = routeToFeature[route];
        if (!feature) return true;
        return hasFeature(feature);
    }, [user, isAdmin, isDemo, hasFeature, filialAtiva]);

    const isDemoRoute = useCallback((route: string): boolean => {
        return DEMO_ALLOWED_ROUTES.includes(route);
    }, []);

    // Aplica a sessao devolvida pela API (login, cadastro ou aceite de convite).
    const aplicarSessao = useCallback(async (response: Awaited<ReturnType<typeof api.login>>) => {
        safeLocalStorage.setItem('accessToken', response.accessToken);
        safeLocalStorage.setItem('refreshToken', response.refreshToken);
        // Cada login deve poder reexibir o toast de vencimento
        limparAvisoDeVencimento();

        const userData = response.user;
        setUser({
            id: userData.id,
            name: userData.name,
            email: userData.email,
            role: userData.role as 'ADMIN' | 'GERENTE' | 'OPERADOR',
            status: userData.status?.toLowerCase() as 'active' | 'inactive' | 'suspended',
            planId: userData.planId,
            onboardingCompletedAt: userData.onboardingCompletedAt ?? null,
            surveyOnboardingRequired: userData.surveyOnboardingRequired ?? false,
            surveyOnboardingCompletedAt: userData.surveyOnboardingCompletedAt ?? null,
        });
        setCurrentPlan(
            userData.plan
                ? {
                    id: userData.plan.id,
                    name: userData.plan.name,
                    description: userData.plan.description || '',
                    price: userData.plan.price,
                    interval: userData.plan.interval ?? 'monthly',
                    features: userData.plan.features || [],
                    isActive: userData.plan.isActive,
                    maxAccounts: userData.plan.maxAccounts ?? 0,
                    maxTransactionsPerMonth: userData.plan.maxTransactionsPerMonth ?? 0,
                    createdAt: userData.plan.createdAt ?? '',
                    updatedAt: userData.plan.updatedAt ?? '',
                }
                : null,
        );

        await carregarFiliais();
        return userData;
    }, [carregarFiliais]);

    const login = useCallback(async (email: string, password: string) => {
        setIsLoading(true);
        try {
            const userData = await aplicarSessao(await api.login(email, password));

            // Quem veio de um link de convite volta para aceitar (ver ConviteAceite).
            const convite = destinoDoConvitePendente();
            if (convite) {
                navigate(convite);
                return;
            }

            const role = userData.role?.toLowerCase();
            if (role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/graficos');
            }
        } finally {
            setIsLoading(false);
        }
    }, [aplicarSessao, navigate]);

    const register = useCallback(async (data: { name: string; email: string; password: string; whatsapp: string }) => {
        setIsLoading(true);
        try {
            await aplicarSessao(await api.register(data));
            navigate('/home');
        } finally {
            setIsLoading(false);
        }
    }, [aplicarSessao, navigate]);

    /** Entra com uma sessao ja emitida pela API (cadastro pelo convite). */
    const entrarComSessao = useCallback(async (response: Awaited<ReturnType<typeof api.login>>) => {
        await aplicarSessao(response);
    }, [aplicarSessao]);
    const selecionarFilial = useCallback(async (filialId: string) => {
        if (filialId === 'all') {
            setFilialAtiva(VISAO_CONSOLIDADA);
            safeLocalStorage.setItem('filialAtiva', valorSalvoDaVisaoConsolidada());
            return;
        }

        const filial = filiais.find(f => f.id === filialId);
        if (filial) {
            setFilialAtiva({ id: filial.id, name: filial.name });
            safeLocalStorage.setItem('filialAtiva', JSON.stringify({ id: filial.id, name: filial.name }));
        }
    }, [filiais]);

    // Trocar de empresa muda plano, negocios e permissoes de uma vez: abre a
    // Matriz dela e volta para o inicio (inclusive de telas so do dono, como
    // Usuarios e Perfis de acesso, que a outra empresa pode nao ter).
    const selecionarEmpresa = useCallback((empresaId: string) => {
        if (!empresas.some(e => e.id === empresaId)) return;
        setEmpresaAtivaId(empresaId);
        safeLocalStorage.setItem(EMPRESA_ATIVA_KEY, valorSalvoDaEmpresa(empresaId));

        const matriz = negocioPadrao(filiaisDaEmpresa(todasAsFiliais, empresaId));
        setFilialAtiva(matriz);
        if (matriz) {
            safeLocalStorage.setItem('filialAtiva', JSON.stringify(matriz));
        } else {
            safeLocalStorage.removeItem('filialAtiva');
        }
        navigate('/home');
    }, [empresas, todasAsFiliais, navigate]);

    const bloqueio = bloqueioDaTela({ contaSuspensa, statusDoUsuario: user?.status, empresaAtiva });

    const logout = useCallback(() => {
        safeLocalStorage.removeItem('accessToken');
        safeLocalStorage.removeItem('refreshToken');
        safeLocalStorage.removeItem('filialAtiva');
        safeLocalStorage.removeItem(EMPRESA_ATIVA_KEY);
        // Limpa o guard do toast de vencimento para reaparecer no próximo login
        limparAvisoDeVencimento();
        setUser(null);
        setCurrentPlan(null);
        setFilialAtiva(null);
        setTodasAsFiliais([]);
        setEmpresas([]);
        setEmpresaAtivaId(null);
        setEmpresasCarregadas(false);
        setContaSuspensa(false);
        setPermissions(null);
    }, []);

    const markOnboardingComplete = useCallback(async () => {
        await api.markOnboardingComplete();
        setUser(prev => prev ? { ...prev, onboardingCompletedAt: new Date().toISOString() } : prev);
    }, []);

    const updateUser = useCallback((data: Partial<User>) => {
        setUser(prev => prev ? { ...prev, ...data } : prev);
    }, []);

    if (!isInitialized) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#0e1a15',
                color: '#fff'
            }}>
                Carregando...
            </div>
        );
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                isAdmin,
                isGerente,
                isDemo,
                currentPlan,
                login,
                register,
                logout,
                hasFeature,
                canAccess,
                isDemoRoute,
                filialAtiva,
                filiais,
                selecionarFilial,
                isLoadingFiliais,
                carregarFiliais,
                markOnboardingComplete,
                updateUser,
                permissions,
                isLoadingPermissions,
                hasPermission,
                empresas,
                empresaAtiva,
                souDonoDaEmpresaAtiva: !!empresaAtiva?.souDono,
                // Mesma regra do backend (EmpresasService.exigirDono).
                podeGerirAcessos: podeGerirAcessosNaEmpresa(empresaAtiva, isAdmin),
                selecionarEmpresa,
                bloqueio,
                semEmpresa,
                criarMinhaEmpresa,
                entrarComSessao,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}